/**
 * Ein Kunde — Entwurf B (Benjamin, 03.11.2026): „Diese Übersicht muss verbessert
 * werden, so dass der User wirklich nur sieht, was er sehen möchte, ohne überfordert
 * zu werden."
 *
 * Oben Kreis, Name und eine Zeile Herkunft; darunter „Jetzt wichtig", wenn etwas
 * ansteht (`lib/kundeLage.ts`). Statt einer langen Seite vier Reiter: Überblick,
 * Projekte, Daten, Verlauf. Anrufen, Mail, Nachrichten und Notizen schweben als
 * Leiste unten am Daumen. Geändert wird an einer Stelle: im Blatt
 * `KundeDatenBlatt`, das jeder „Bearbeiten"-Knopf auf seinem Bereich öffnet.
 *
 * Steht auf der Kundenseite neben der Liste und im Kunden-Blatt (Aufgaben,
 * Nachrichten). Bis 03.11.2026 war jeder Abschnitt einzeln bearbeitbar
 * (Fassung vom 03.10.2026) — die Felder sind dieselben geblieben.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteKunde, useKundePflegevertraege, useKundeProjekte } from "../../hooks/usePlanung";
import { useKundenLage } from "../../hooks/useKundenLage";
import { errorText } from "../../lib/invoices";
import { normAddr, rowToForm } from "../../lib/kundeFormular";
import { kreisTon, kuerzel, pflegeText, tageSeit } from "../../lib/kundeLage";
import { statusTon, statusWort } from "../../lib/projekte";
import type { Kunde } from "../../lib/types";
import { useNachrichtenFenster } from "../nachrichten/NachrichtenKontext";
import ProjektNeuDialog from "../projekt/ProjektNeuDialog";
import KundeAktivitaet from "./KundeAktivitaet";
import KundeDaten from "./KundeDaten";
import KundeDatenBlatt, { type DatenBereich } from "./KundeDatenBlatt";
import KundeUeberblick from "./KundeUeberblick";
import { MailKnopf, TelefonLink, kundeHauptnummer, kundeMailZiele } from "./KontaktAktionen";
import { KundeNotizenKnopf } from "./KundeNotizen";
import { ZeichenChat, ZeichenMail, ZeichenMehr, ZeichenNotiz, ZeichenTelefon, ZeichenZu } from "./KundeZeichen";
import "../../styles/lieferanten.css";
import "@vimaana/ui/kopf";
import "../../styles/kunden.css";
import "../../styles/kundenfluss.css";

type Props = { kunde: Kunde; onSchliessen: () => void };
type Reiter = "ueberblick" | "projekte" | "daten" | "verlauf";

export default function KundeAnsicht({ kunde, onSchliessen }: Props) {
  const id = kunde.id;
  const qc = useQueryClient();
  const nachrichten = useNachrichtenFenster();
  const del = useDeleteKunde();
  const projekteQ = useKundeProjekte(id);
  const pflegeQ = useKundePflegevertraege(id);
  const lage = useKundenLage().nachId.get(id);

  const stand = useMemo(() => rowToForm(kunde), [kunde]);
  const [reiter, setReiter] = useState<Reiter>("ueberblick");
  const [blatt, setBlatt] = useState<DatenBereich | null>(null);
  const [menue, setMenue] = useState(false);
  const [loeschen, setLoeschen] = useState(false);
  const [projektNeu, setProjektNeu] = useState(false);
  const [fehler, setFehler] = useState("");
  const menueRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menue) return;
    const zu = (e: MouseEvent) => { if (!menueRef.current?.contains(e.target as Node)) { setMenue(false); setLoeschen(false); } };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, [menue]);

  const privat = stand.kundentyp === "privat";
  const personen = stand.kontakte.filter((k) => k.art === "person");
  const mitPersonen = !privat || personen.length > 0;
  const projekte = projekteQ.data ?? [];
  const laufend = projekte.filter((p) => !["grau", "rot"].includes(statusTon(p.status)));
  const sortiert = [...laufend, ...projekte.filter((p) => !laufend.includes(p))];
  const kannLoeschen = !projekteQ.isPending && !projekteQ.isError && projekte.length === 0;
  const nummer = kundeHauptnummer(kunde);
  const ziele = kundeMailZiele(kunde);
  const ort = (stand.ort || normAddr(kunde.lieferadresse || kunde.adresse || "").ort || "").trim();
  const pflege = pflegeText(stand.pflege_monate);
  const angebotTage = lage?.angebot_seit ? tageSeit(lage.angebot_seit) : 0;

  const herkunft = [privat ? "Privat" : "Firma", `Nr. ${id}`, ort].filter(Boolean).join(" · ");
  const zeigen = (r: Reiter) => setReiter(r);

  const reiterListe: { id: Reiter; label: string }[] = [
    { id: "ueberblick", label: "Überblick" },
    { id: "projekte", label: projekte.length ? `Projekte ${projekte.length}` : "Projekte" },
    { id: "daten", label: "Daten" },
    { id: "verlauf", label: "Verlauf" },
  ];

  return (
    <div className="ku-ansicht">
      <header className="ku-kopf">
        <span className={`ku-kreis is-riesig is-${kreisTon(id)}`} aria-hidden="true">{kuerzel(kunde.name)}</span>
        <div className="ku-kopf-text">
          <h2>{kunde.name || "Ohne Namen"}</h2>
          <p>{herkunft}{pflege && <span className="ku-marke is-gruen">Pflege · {pflege}</span>}</p>
        </div>
        <div className="ku-kopf-tat">
          <div className="pk-menue" ref={menueRef}>
            <button type="button" className="ku-rund" aria-label="Weitere Aktionen zum Kunden" aria-haspopup="menu" aria-expanded={menue}
              onClick={() => { setMenue((v) => !v); setLoeschen(false); }}><ZeichenMehr /></button>
            {menue && (
              <div className="pk-menue-liste" role="menu">
                <button type="button" role="menuitem" className="pk-menue-punkt" onClick={() => { setMenue(false); setBlatt("name"); }}>Name &amp; Art ändern</button>
                <button type="button" role="menuitem" className="pk-menue-punkt" onClick={() => { setMenue(false); setProjektNeu(true); }}>Neues Projekt</button>
                <div className="pk-menue-trenner" />
                {!kannLoeschen ? (
                  <p className="pk-menue-status">
                    {projekteQ.isPending ? "Projekte werden noch geladen …"
                      : projekteQ.isError ? "Projekte nicht geladen — Löschen ist vorsichtshalber gesperrt."
                      : `Löschen geht erst, wenn die ${projekte.length} Projekt(e) umgehängt oder gelöscht sind.`}
                  </p>
                ) : !loeschen ? (
                  <button type="button" role="menuitem" className="pk-menue-punkt is-gefahr" onClick={() => setLoeschen(true)}>Kunde löschen</button>
                ) : (
                  <div className="pk-menue-loeschen">
                    <span>Sicher?</span>
                    <button type="button" className="lf-knopf is-gefahr" disabled={del.isPending}
                      onClick={() => del.mutate(id, { onSuccess: onSchliessen, onError: (err) => setFehler(errorText(err) || "Löschen fehlgeschlagen.") })}>
                      {del.isPending ? "…" : "Ja, löschen"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <button type="button" className="ku-rund" aria-label="Schließen" onClick={onSchliessen}><ZeichenZu /></button>
        </div>
      </header>
      {fehler && <p className="lf-fehler" role="alert">{fehler}</p>}

      {lage && lage.aufgaben > 0 ? (
        <div className="ku-wichtig">
          <div className="ku-wichtig-text">
            <span className="ku-wichtig-ueber">Jetzt wichtig</span>
            <strong>{lage.aufgabe}</strong>
            {lage.aufgaben > 1 && <span>und {lage.aufgaben - 1} weitere {lage.aufgaben === 2 ? "Aufgabe" : "Aufgaben"}</span>}
          </div>
          <button type="button" className="ku-knopf is-hell" onClick={() => {
            setReiter("ueberblick");
            window.requestAnimationFrame(() => document.getElementById("ku-aufgaben")?.scrollIntoView({ behavior: "smooth", block: "start" }));
          }}>Ansehen</button>
        </div>
      ) : angebotTage >= 3 ? (
        <div className="ku-wichtig">
          <div className="ku-wichtig-text">
            <span className="ku-wichtig-ueber">Jetzt wichtig</span>
            <strong>Angebot ohne Antwort</strong>
            <span>seit {angebotTage} Tagen</span>
          </div>
          {ziele[0] && <MailKnopf email={ziele[0].email} kundeId={id} kundeName={kunde.name} ziele={ziele} className="ku-knopf is-hell">Nachfassen</MailKnopf>}
        </div>
      ) : lage && lage.rechnungen_offen > 0 ? (
        <div className="ku-wichtig">
          <div className="ku-wichtig-text">
            <span className="ku-wichtig-ueber">Jetzt wichtig</span>
            <strong>{lage.rechnungen_offen === 1 ? "Eine Rechnung ist offen" : `${lage.rechnungen_offen} Rechnungen sind offen`}</strong>
          </div>
          <Link className="ku-knopf is-hell" to="/rechnungen">Ansehen</Link>
        </div>
      ) : null}

      <div className="ku-reiter" role="tablist" aria-label="Bereiche des Kunden">
        {reiterListe.map((r) => (
          <button key={r.id} type="button" role="tab" aria-selected={reiter === r.id} className={reiter === r.id ? "ist-an" : ""} onClick={() => zeigen(r.id)}>{r.label}</button>
        ))}
      </div>

      <div className="ku-reiter-inhalt" role="tabpanel">
        {reiter === "ueberblick" && (
          <KundeUeberblick kunde={kunde} stand={stand} laufend={laufend} onBearbeiten={setBlatt} onAlleProjekte={() => zeigen("projekte")} />
        )}
        {reiter === "projekte" && (
          <div className="ku-stapel">
            <div className="ku-zeilen-kopf">
              <span>{projekteQ.isPending ? "Lade …" : projekte.length ? `${laufend.length} laufend · ${projekte.length} insgesamt` : "Noch kein Projekt."}</span>
              <button type="button" className="ku-bearbeiten" onClick={() => setProjektNeu(true)}>＋ Projekt</button>
            </div>
            {projekteQ.isError && <p className="lf-fehler">Projekte konnten nicht geladen werden.</p>}
            {sortiert.map((p) => {
              const ton = statusTon(p.status);
              return (
                <Link key={p.id} to={`/projekte/${p.id}`} className={"ku-projekt-karte" + (ton === "grau" || ton === "rot" ? " ist-alt" : "")}>
                  <span className={`pk-status-punkt is-${ton}`} aria-hidden="true" />
                  <span className="ku-projekt-text">
                    <span className="ku-projekt-titel">{p.title || `Projekt ${p.id}`}</span>
                    <span className="ku-projekt-status">{statusWort(p.status)}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        )}
        {reiter === "daten" && (
          <KundeDaten kunde={kunde} stand={stand} mitPersonen={mitPersonen} vertraege={pflegeQ.data} onBearbeiten={setBlatt} />
        )}
        {reiter === "verlauf" && <div className="ku-gruppe-karte"><KundeAktivitaet key={id} kundeId={id} /></div>}
      </div>

      <div className="ku-aktionen" role="toolbar" aria-label="Kunde erreichen">
        {nummer
          ? <TelefonLink nummer={nummer} className="ku-aktion is-anruf"><ZeichenTelefon /><span className="ku-unsichtbar">Anrufen</span></TelefonLink>
          : <button type="button" className="ku-aktion" disabled aria-label="Anrufen — keine Nummer hinterlegt"><ZeichenTelefon /></button>}
        {ziele.length > 0
          ? <MailKnopf email={ziele[0].email} kundeId={id} kundeName={kunde.name} ziele={ziele} className="ku-aktion"><ZeichenMail /><span className="ku-unsichtbar">Mail schreiben</span></MailKnopf>
          : <button type="button" className="ku-aktion" disabled aria-label="Mail — keine Adresse hinterlegt"><ZeichenMail /></button>}
        <button type="button" className="ku-aktion" aria-label="Nachrichten" title="Nachrichten" onClick={() => nachrichten.oeffnen({ schluessel: `kunde:${id}` })}><ZeichenChat /></button>
        <KundeNotizenKnopf kundeId={id} kundeName={kunde.name} className="ku-aktion" symbol={<ZeichenNotiz />} nurZeichen />
      </div>

      {blatt && <KundeDatenBlatt kunde={kunde} start={blatt} mitPersonen={mitPersonen} onClose={() => setBlatt(null)} />}
      {projektNeu && (
        <ProjektNeuDialog
          kundeId={id}
          hinweis="landet in der Projektliste und steht gleich hier beim Kunden"
          onSchliessen={() => setProjektNeu(false)}
          onAngelegt={() => {
            setProjektNeu(false);
            qc.invalidateQueries({ queryKey: ["kunde", id, "projekte"] });
            qc.invalidateQueries({ queryKey: ["kunden", "lage"] });
          }}
        />
      )}
    </div>
  );
}
